import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withKeys as _withKeys, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableCustomLarge, { TableItem } from '@/design-system/molecules/TableCustomLarge.vue'
import { ActCategory, getActCategories } from '@/services/acts.service'
import { Attachment, Direction } from '@/services/attachments.service'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { getPoiById } from '@/services/pois.service'
import { getRealmId } from '@/utils/realmId'
import { getLineLayerById } from '@/services/lines.service'
import { getZoneLayerById } from '@/services/zones.service'

type Props = {
  fields: any[]
  items?: Attachment[]
  resetSorting?: boolean
}

export default /*@__PURE__*/_defineComponent({
  __name: 'TableFilesLarge',
  props: {
    fields: {},
    items: {},
    resetSorting: { type: Boolean }
  },
  emits: ["archiveFile", "deleteFile", "downloadFile", "selectedFiles", "sortBy"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props
const emits = __emit

const { t } = useI18n()

const piniaStore = useGeneralStore()
const coreEntityStore = useCoreEntityStore()
const store = useStore()

const coreEntityTypes = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']

const actCategories = ref<ActCategory[] | null>(null)
const parentNames = ref<Record<string, string>>({})
const isLoading = ref(false)
const filesTable = ref()
const selectedFiles = ref<Attachment[]>([])
const tableItems = ref<TableItem[]>([])

const allFilesSelected = computed(() => {
  const filePageIds = itemsProp.value?.map((file) => file.attachmentId)
  const selectedIds = selectedFiles.value.map((file) => file.attachmentId)
  return filePageIds?.every((id) => selectedIds.includes(id))
})
const containsActs = computed<boolean>(() => {
  return itemsProp.value?.find((item) => item.actType) ? true : false
})
const itemsProp = computed(() => props.items)
const selectedIds = computed(() => {
  return selectedFiles.value.map((attachment) => attachment.attachmentId)
})
const tableFields = computed(() => {
  return props.fields
})

function getActTypeName(actTypeId: string) {
  const allTypes = actCategories.value?.flatMap((category) => category.types)
  const foundType = allTypes?.find((type) => type.id === actTypeId)
  return foundType?.name
}
function getActStatusName(actStatusId: string) {
  const allStatuses = actCategories.value?.flatMap((category) => category.statuses)
  const foundStatus = allStatuses?.find((type) => type.id === actStatusId)
  return foundStatus?.name
}
async function getEntityParents(item: TableItem): Promise<string | undefined> {
  try {
    if (!item.entityId) return undefined
    let parents
    if (coreEntityTypes.includes(item.entityType)) {
      const currentEntity = await coreEntityStore.getCoreEntity(item.entityId)
      const parentNames = await coreEntityStore.getParents(item.entityId)
      parents = [...parentNames, currentEntity]
    } else if (item.entityType === 'POI') {
      const poi = await getPoiById(item.entityId)
      if (!poi) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(poi.coreId)
      const parentNames = await coreEntityStore.getParents(poi.coreId)
      parents = [...parentNames, currentEntity, poi]
    } else if (item.entityType === 'ZONE') {
      const zone = await getZoneLayerById(item.entityId)
      if (!zone) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(zone.coreId)
      const parentNames = await coreEntityStore.getParents(zone.coreId)
      parents = [...parentNames, currentEntity, zone]
    } else if (item.entityType === 'LINE') {
      const line = await getLineLayerById(item.entityId)
      if (!line) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(line.coreId)
      const parentNames = await coreEntityStore.getParents(line.coreId)
      parents = [...parentNames, currentEntity, line]
    } else {
      // case realm
      parents = [{ name: getRealmId() }]
    }
    if (!parents) return undefined
    const parentNames = [...parents].map((parent) => parent.name).join(', ')
    return parentNames
  } catch (error) {
    isLoading.value = false
    return undefined
  }
}
function handleCheckboxAll() {
  if (allFilesSelected.value) {
    itemsProp.value?.forEach((file) => {
      const index = selectedFiles.value.findIndex(
        (attachment) => attachment.attachmentId === file.attachmentId
      )
      selectedFiles.value.splice(index, 1)
    })
  } else {
    itemsProp.value?.forEach((file) => {
      if (!selectedFiles.value.find((attachment) => attachment.attachmentId === file.attachmentId))
        selectedFiles.value.push(file)
    })
  }
  emits('selectedFiles', selectedFiles.value)
  if (selectedFiles.value.length === 1) {
    piniaStore.setSelectedFile(selectedFiles.value[0])
  } else piniaStore.setSelectedFile(null)
}
function handleSorting(key: string, value: Direction) {
  emits('sortBy', key, value)
}
function handleSpaceKey(event: KeyboardEvent, item) {
  event.preventDefault()
  onRowClick(item, true)
}
function onRowClick(item: TableItem, withinCheckbox: boolean) {
  if (!itemsProp.value) return
  let file = itemsProp.value.find((file) => file.attachmentId === item.id)
  if (!file) return
  if (!withinCheckbox) {
    // If the row (not checkbox) was clicked, set single selected file in store
    if (
      // If there was one selected file and the file is selected again
      selectedFiles.value.length === 1 &&
      piniaStore?.selectedFile?.attachmentId === file.attachmentId
    ) {
      selectedFiles.value = []
      piniaStore.setSelectedFile(null)
    } else {
      // Remove selections and set single selected file in store
      selectedFiles.value = [file]
      piniaStore.setSelectedFile(file)
    }
    emits('selectedFiles', selectedFiles.value)
    return
  }
  if (
    // If there was one selected file and the file is selected again
    selectedFiles.value.length === 1 &&
    piniaStore?.selectedFile?.attachmentId === file.attachmentId
  ) {
    selectedFiles.value = []
    piniaStore.setSelectedFile(null)
  } else if (
    // If there were selected files and the file is not already selected
    selectedFiles.value.length >= 1 &&
    !selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    selectedFiles.value.push(file)
    piniaStore.setSelectedFile(null)
  } else if (
    // If there are more than 2 selected files and the file is already selected
    selectedFiles.value.length > 2 &&
    selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    const index = selectedFiles.value.findIndex(
      (attachment) => attachment.attachmentId === file?.attachmentId
    )
    selectedFiles.value.splice(index, 1)
  } else if (
    // If there were 2 selected files and the file is already selected
    selectedFiles.value.length === 2 &&
    selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    const index = selectedFiles.value.findIndex(
      (attachment) => attachment.attachmentId === file?.attachmentId
    )
    selectedFiles.value.splice(index, 1)
    piniaStore.setSelectedFile(selectedFiles.value[0]) // Set selected to the remaining file
  } else {
    // If there were no selected files
    selectedFiles.value.push(file)
    piniaStore.setSelectedFile(file)
  }
  emits('selectedFiles', selectedFiles.value)
}
function resetSelections() {
  selectedFiles.value = []
  emits('selectedFiles', [])
  piniaStore.setSelectedFile(null)
}
function setSelectedFilesFromDashboard(selectedDashboardFiles: Attachment[]) {
  selectedFiles.value = selectedDashboardFiles
  emits('selectedFiles', selectedFiles.value)
}
watch(
  itemsProp,
  async (newValue) => {
    tableItems.value = []
    if (containsActs.value) {
      const actCategoriesResponse = await getActCategories()
      actCategories.value = actCategoriesResponse ?? null
    }
    if (newValue?.length) {
      const itemsArray: TableItem[] = []
      newValue.forEach((item) => {
        itemsArray.push({
          id: item.attachmentId,
          name: item.name,
          entityId: item.entityId,
          createdDate: item.createdDate,
          entityType: item.entityType,
          actType: item.actType ? getActTypeName(item.actType) : undefined,
          actStatus: item.actStatus ? getActStatusName(item.actStatus) : undefined
        })
      })
      tableItems.value = itemsArray
    }
  },
  { immediate: true }
)
watch(
  tableItems,
  async (newValue) => {
    let names = newValue.map(async (item) => {
      if (!item.entityId) return
      isLoading.value = true
      return [item.entityId, await getEntityParents(item)]
    })
    let prom = (await Promise.all(names)) as [string, string | undefined][]
    parentNames.value = Object.fromEntries(
      prom.filter((item) => item[1]).map((item) => item as [string, string])
    )
    isLoading.value = false
  },
  { immediate: true }
)
__expose({
  resetSelections,
  setSelectedFilesFromDashboard
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TableCustomLarge, {
    ref_key: "filesTable",
    ref: filesTable,
    class: "table-custom-large",
    fields: tableFields.value,
    hasArchiveButton: _unref(store).getters.hasFileEditorAccess,
    hasCreateButton: _unref(store).getters.hasFileCreatorAccess,
    hasDeleteButton: _unref(store).getters.hasFileAdminAccess,
    hasDownloadButton: _unref(store).getters.hasFileReaderAccess,
    items: tableItems.value,
    itemType: _unref(t)('general__data__files'),
    selectedIds: selectedIds.value,
    resetSorting: _ctx.resetSorting,
    onArchiveItem: _cache[2] || (_cache[2] = ($event: any) => (emits('archiveFile'))),
    onDeleteItem: _cache[3] || (_cache[3] = ($event: any) => (emits('deleteFile'))),
    onDownloadItem: _cache[4] || (_cache[4] = ($event: any) => (emits('downloadFile'))),
    onOnClickedRow: _cache[5] || (_cache[5] = (item, withinCheckbox) => onRowClick(item, withinCheckbox)),
    onSortBy: handleSorting
  }, {
    "checkbox-heading": _withCtx(() => [
      _createVNode(CheckBoxStandard, {
        checked: allFilesSelected.value,
        onValueChanged: _cache[0] || (_cache[0] = ($event: any) => (handleCheckboxAll()))
      }, null, 8, ["checked"])
    ]),
    "checkbox-data": _withCtx(({ item }) => [
      _createVNode(CheckBoxStandard, {
        checked: selectedFiles.value.find((file) => file.attachmentId === item.id) ? true : false,
        style: {"z-index":"2"},
        onClick: _cache[1] || (_cache[1] = (event) => event.preventDefault()),
        onKeydown: _withKeys((event) => handleSpaceKey(event, item), ["space"])
      }, null, 8, ["checked", "onKeydown"])
    ]),
    placement: _withCtx(({ item }) => [
      (isLoading.value)
        ? (_openBlock(), _createBlock(PulseLoader, {
            key: 0,
            loading: true,
            color: "#3F46D6"
          }))
        : (item.entityId && parentNames.value[item.entityId] && !isLoading.value)
          ? (_openBlock(), _createBlock(BadgeSquare, {
              key: 1,
              badgeText: parentNames.value[item.entityId],
              grey: true,
              size: "small"
            }, null, 8, ["badgeText"]))
          : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["fields", "hasArchiveButton", "hasCreateButton", "hasDeleteButton", "hasDownloadButton", "items", "itemType", "selectedIds", "resetSorting"]))
}
}

})